import { useCallback, useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import {
  GetAssetsForMapDocument,
  GetAssetsForMapQuery,
  GetAssetsForMapQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

// Utility function to check if a string looks like a UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
 * Hook for fetching assets optimized for map display with filtering and bounds calculation
 *
 * Provides filtering functionality and auto-calculated map bounds.
 * Includes background polling for real-time updates.
 */
export function useAssetsForMap() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterVersion, setFilterVersion] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)

  const [filters, setFilters] = useState<{
    asset?: string
    customer?: string
    site?: string
    alertStatus?: string
    alertSeverity?: string
    hasAlerts?: boolean
  }>(() => {
    // Initialise filters from URL search params with validation
    const asset = searchParams.get("asset") || undefined
    const customer = searchParams.get("customer") || undefined
    const site = searchParams.get("site") || undefined
    const alertStatus = searchParams.get("status") || undefined
    const hasAlerts = searchParams.get("has_alerts") !== null ? Boolean(searchParams.get("has_alerts")) : undefined

    // Validate that entity parameters are UUIDs, warn if not
    if (customer && !isValidUUID(customer)) {
      console.warn(
        `Warning: Customer parameter "${customer}" from URL does not appear to be a valid UUID. This may cause backend errors.`,
      )
    }
    if (site && !isValidUUID(site)) {
      console.warn(
        `Warning: Site parameter "${site}" from URL does not appear to be a valid UUID. This may cause backend errors.`,
      )
    }
    if (asset && !isValidUUID(asset)) {
      console.warn(
        `Warning: Asset parameter "${asset}" from URL does not appear to be a valid UUID. This may cause backend errors.`,
      )
    }

    return { asset, customer, site, alertStatus, hasAlerts }
  })

  // Apollo query with current parameters and polling
  // Use cache-first to avoid loading flicker on filter changes, with polling for updates
  const [getAssetsForMap, { data, loading, refetch, error, previousData }] = useLazyQueryWithEndpoint<
    GetAssetsForMapQuery,
    GetAssetsForMapQueryVariables
  >(
    GetAssetsForMapDocument,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true, // Enable to track network status
      pollInterval: 30000, // Poll every 30 seconds for real-time updates
    },
    "primary",
  )

  // Trigger query when filters change
  useEffect(() => {
    console.log("filters", filters)

    // Set refetching state if we have previous data
    if (previousData) {
      setIsRefetching(true)
    }

    getAssetsForMap({
      variables: {
        asset: filters.asset || null,
        customer: filters.customer || null,
        site: filters.site || null,
        alertStatus: filters.alertStatus === "no_alerts" ? null : filters.alertStatus || null,
        alertSeverity: filters.alertSeverity || null,
        hasAlerts: filters.hasAlerts !== undefined ? filters.hasAlerts : null,
      },
    })
  }, [filters, getAssetsForMap, previousData])

  // Clear refetching state when loading completes
  useEffect(() => {
    if (!loading && isRefetching) {
      setIsRefetching(false)
    }
  }, [loading, isRefetching])

  // Use previous data while loading to prevent flickering
  const effectiveData = data || previousData

  // Update URL when filters change
  useEffect(() => {
    setSearchParams(
      (params) => {
        if (filters.asset) params.set("asset", filters.asset)
        else params.delete("asset")
        if (filters.customer) params.set("customer", filters.customer)
        else params.delete("customer")
        console.log(filters.site)
        if (filters.site) params.set("site", filters.site)
        else params.delete("site")
        if (filters.alertStatus) params.set("status", filters.alertStatus)
        else params.delete("status")
        console.log(filters.hasAlerts)
        if (filters.hasAlerts !== undefined) params.set("has_alerts", filters.hasAlerts.toString())
        else params.delete("has_alerts")
        params.set("filter_version", (filterVersion + 1).toString())
        return params
      },
      { replace: true },
    )
    setFilterVersion(filterVersion + 1)
  }, [filters])

  // Handle filters change
  const handleFiltersChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters)
  }, [])

  // Clear all filters
  const handleClearAll = useCallback(() => {
    setFilters({})
  }, [])

  // Handle refetch
  const handleRefetch = useCallback(() => {
    refetch()
  }, [refetch])

  // Only show loading on initial load (no previous data)
  const isInitialLoading = loading && !previousData

  return {
    assets: effectiveData?.getAssetsForMap?.assets || [],
    mapBounds: effectiveData?.getAssetsForMap?.mapBounds,
    headlines: effectiveData?.getAssetsForMap?.headlines,
    filterOptions: effectiveData?.getAssetsForMap?.filterOptions,
    loading: isInitialLoading,
    isRefetching,
    error,
    filters,
    setFilters: handleFiltersChange,
    clearAll: handleClearAll,
    refetch: handleRefetch,
  }
}
